import { useCallback, useState } from 'react';

import { ListingType } from 'algolia-api/types/ListingType';
import { showModal } from 'modules/main/store/ducks/modal.actions';
import {
  saveListing,
  unsaveListing,
} from 'modules/main/store/ducks/userEnvironment.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';

export type SaveListingType = (
  e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
) => Promise<void>;

type UseSaveListingProps = {
  listingType: ListingType | 'IMPORTED';
  listingName: string;
  listingId: string;
  orgId?: string | null;
  orgName?: string | null;
  orgType?: string | null;
};

export const useSaveListing = ({
  listingType,
  listingName,
  listingId,
  orgId,
  orgName,
  orgType,
}: UseSaveListingProps) => {
  const [showOrgModal, setShowOrgModal] = useState(false);
  const dispatch = useMainDispatch();
  const { user, isUserLoaded } = useUserEnvironment();

  const loggedIn = Boolean(user);
  const saved =
    user?.savedListings.some((listing) => listing.id === listingId) || false;

  const saveOrg = useCallback(async () => {
    if (orgId && orgName) {
      await dispatch(saveListing(orgId, 'ORG'));
      setShowOrgModal(false);
    }
  }, [dispatch, orgId, orgName]);

  const save: SaveListingType = useCallback(async () => {
    if (loggedIn) {
      await dispatch(saveListing(listingId, listingType));
      const savedOrg = orgId && user?.savedListings.some((l) => l.id === orgId);

      if (
        !savedOrg &&
        orgId &&
        orgName &&
        listingType &&
        ['JOB', 'VOLOP', 'INTERNSHIP'].includes(listingType)
      ) {
        setShowOrgModal(true);
      }
    } else {
      dispatch(
        showModal('EMAIL_SIGNUP', {
          // @ts-expect-error Need to update `ListingType` to account for USER
          title: `Save this ${LISTING_TYPE_HUMAN_NAME_MAP[listingType]}`,
          savedListing: {
            id: listingId,
            type: listingType,
            name: listingName,
          },
          redirectTo: `${window.location.pathname}${window.location.search}`,
        }),
      );
    }

    trackClicked('Save Listing', {
      // @ts-expect-error need to update ListingType type to account for `USER`
      listing_type: LISTING_TYPE_HUMAN_NAME_MAP[listingType],
      listing_id: listingId,
      org_id: orgId,
      org_type: orgType,
    });
  }, [
    loggedIn,
    dispatch,
    listingId,
    listingType,
    listingName,
    orgId,
    orgName,
    orgType,
    user?.savedListings,
  ]);

  const unsave = useCallback(async () => {
    await dispatch(unsaveListing(listingId, listingType));
  }, [dispatch, listingId, listingType]);

  return {
    saved,
    saveOrg,
    setShowOrgModal,
    showOrgModal,
    canSave: isUserLoaded && listingType !== 'IMPORTED',
    saveListing: save,
    unsaveListing: unsave,
  };
};
